import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Navbar from "../Components/NavBar";
import "../css/listingAdmin.css";

const emptyRow = () => ({
  id: crypto.randomUUID(),
  status: "active",
  address: "",
  city: "",
  state: "KY",
  zip: "",
  price: "",
  beds: "",
  baths: "",
  sqft: "",
  image: "",
  url: "",
  soldDate: "",
  sort: "",
});

const norm = (s) => String(s || "").toLowerCase().trim();

// A row is worth persisting if any identifying field is filled in;
// keeps abandoned blank rows out of the sheet.
const isRealRow = (x) =>
  [x?.address, x?.city, x?.image, x?.url, x?.price].some((v) => String(v || "").trim());

const inputStyle = {
  width: "100%",
  padding: "0.8rem 0.9rem",
  borderRadius: 14,
  border: "1px solid rgba(0,0,0,0.12)",
  outline: "none",
  fontSize: "1rem",
  background: "#fff",
};

const Field = React.memo(function Field({ label, children, className = "" }) {
  return (
    <div className={className} style={{ display: "grid", gap: 6, alignContent: "start" }}>
      <div style={{ fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--text-muted)" }}>
        {label}
      </div>
      {children}
    </div>
  );
});

const RowCard = React.memo(function RowCard({ item, onOpen, onDelete }) {
  const title = item.address?.trim() ? item.address : "New Listing";
  const subtitle = `${item.city || ""}${item.city ? ", " : ""}${item.state || ""} ${item.zip || ""}`.trim();

  return (
    <div className="ui-card" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {subtitle || "—"}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
        <div style={{ fontSize: 12, padding: "6px 10px", borderRadius: 999, border: "1px solid var(--border-light)" }}>
          {(item.status || "active").toUpperCase()}
        </div>

        <button className="btn secondary small" type="button" onClick={() => onOpen(item.id)}>
          Edit
        </button>

        <button className="btn secondary small" type="button" onClick={() => onDelete(item.id)}>
          Delete
        </button>
      </div>
    </div>
  );
});

const EditorModal = React.memo(function EditorModal({ item, onClose, onCommit }) {
  const [draft, setDraft] = useState(item);

  // If user clicks a different item, load it into the draft
  useEffect(() => setDraft(item), [item.id]);

  // Close on Escape and lock page scroll while the modal is open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div
      className="admin-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label="Edit listing">
        <div className="admin-modal__header">
          <h3>{draft.address?.trim() ? draft.address : "New Listing"}</h3>

          <button className="admin-modal__close" type="button" onClick={onClose} aria-label="Close editor">
            &times;
          </button>
        </div>

        <div className="admin-modal__body">
          <div className="admin-modal__grid">
            <Field label="Address" className="admin-modal__field--wide">
              <input value={draft.address || ""} onChange={(e) => set("address", e.target.value)} style={inputStyle} autoFocus />
            </Field>
            <Field label="Status">
              <select
                value={draft.status || "active"}
                onChange={(e) => {
                  const next = e.target.value;
                  set("status", next);
                  if (next !== "sold") set("soldDate", "");
                }}
                style={{ ...inputStyle, padding: "0.8rem 0.75rem" }}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="sold">Sold</option>
              </select>
            </Field>

            <Field label="City">
              <input value={draft.city || ""} onChange={(e) => set("city", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="State">
              <input value={draft.state || ""} onChange={(e) => set("state", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="ZIP">
              <input value={draft.zip || ""} onChange={(e) => set("zip", e.target.value)} style={inputStyle} />
            </Field>

            <Field label="Price (optional)">
              <input value={draft.price || ""} onChange={(e) => set("price", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Beds">
              <input value={draft.beds || ""} onChange={(e) => set("beds", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Baths">
              <input value={draft.baths || ""} onChange={(e) => set("baths", e.target.value)} style={inputStyle} />
            </Field>

            <Field label="Sqft">
              <input value={draft.sqft || ""} onChange={(e) => set("sqft", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Sort (higher shows first)">
              <input value={draft.sort || ""} onChange={(e) => set("sort", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Sold Date (optional)">
              <input value={draft.soldDate || ""} onChange={(e) => set("soldDate", e.target.value)} placeholder="2026-01-17" style={inputStyle} />
            </Field>

            <Field label="Image URL" className="admin-modal__field--full">
              <input value={draft.image || ""} onChange={(e) => set("image", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Details URL" className="admin-modal__field--full">
              <input value={draft.url || ""} onChange={(e) => set("url", e.target.value)} style={inputStyle} />
            </Field>
          </div>
        </div>

        <div className="admin-modal__footer">
          <button className="btn secondary" type="button" onClick={onClose}>
            Cancel
          </button>

          <button
            className="btn primary"
            type="button"
            onClick={() => {
              onCommit(draft);
              onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
});

export default function ListingsAdmin() {
  const [itemsById, setItemsById] = useState(() => new Map());
  const [order, setOrder] = useState([]); // keep a stable ordering
  const [activeTab, setActiveTab] = useState("active"); // "active" | "sold"
  const [openId, setOpenId] = useState(null);

  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [loading, setLoading] = useState(false);

  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);

  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState("");

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setChecking(true);
        const r = await fetch("/api/admin/me", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        if (ok) setUnlocked(!!j.authenticated);
      } catch {
        if (ok) setUnlocked(false);
      } finally {
        if (ok) setChecking(false);
      }
    })();
    return () => { ok = false; };
  }, []);

  useEffect(() => {
    if (!unlocked) return;

    let ok = true;
    (async () => {
      try {
        setLoading(true);
        setStatus({ state: "idle", msg: "" });

        const r = await fetch("/api/listings");
        const j = await r.json();

        if (!ok) return;

        const mergedRaw = [...(j.active || []), ...(j.pending || []), ...(j.sold || [])];
        const merged = mergedRaw.map((row) => ({ ...row, id: row.id || crypto.randomUUID() }));
        const nextMap = new Map();
        const nextOrder = [];

        for (const row of (merged.length ? merged : [emptyRow()])) {
          if (nextMap.has(row.id)) continue; // skip duplicate rows from the sheet
          nextMap.set(row.id, row);
          nextOrder.push(row.id);
        }

        setItemsById(nextMap);
        setOrder(nextOrder);
      } catch {
        if (ok) setStatus({ state: "error", msg: "Failed to load listings." });
      } finally {
        if (ok) setLoading(false);
      }
    })();

    return () => { ok = false; };
  }, [unlocked]);

  const items = useMemo(() => order.map((id) => itemsById.get(id)).filter(Boolean), [order, itemsById]);

  const filtered = useMemo(() => {
    if (activeTab === "sold") return items.filter((x) => norm(x.status) === "sold");
    // treat active tab as: active + pending (common admin expectation)
    return items.filter((x) => {
      const s = norm(x.status) || "active";
      return s === "active" || s === "pending";
    });
  }, [items, activeTab]);

  const counts = useMemo(() => {
    let sold = 0, activePending = 0;
    for (const x of items) {
      const s = norm(x.status) || "active";
      if (s === "sold") sold++;
      else activePending++;
    }
    return { active: activePending, sold };
  }, [items]);

  // Tracks a freshly added row that hasn't been committed yet, so
  // canceling the editor discards it instead of leaving a blank row.
  const pendingNewIdRef = useRef(null);

  const persist = useCallback(async (payloadItems) => {
    setStatus({ state: "loading", msg: "Saving..." });
    try {
      const r = await fetch("/api/listings/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: payloadItems }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Save failed");

      // Drop the public page's snapshot so it refetches fresh data next visit
      try { localStorage.removeItem("khl-listings-cache-v1"); } catch {}

      setStatus({ state: "success", msg: "Saved." });
      return true;
    } catch (e) {
      setStatus({ state: "error", msg: e.message || "Save failed." });
      if ((e.message || "").toLowerCase().includes("unauthorized")) setUnlocked(false);
      return false;
    }
  }, []);

  const commitItem = useCallback((row) => {
    if (pendingNewIdRef.current === row.id) pendingNewIdRef.current = null;

    const nextMap = new Map(itemsById);
    nextMap.set(row.id, row);
    setItemsById(nextMap);

    // ✅ if status moved across tabs, auto-close (or auto-switch tab)
    const s = norm(row.status) || "active";
    if (activeTab === "sold" && s !== "sold") setOpenId(null);
    if (activeTab !== "sold" && s === "sold") setOpenId(null);

    // Persist right away so edits can't be lost by forgetting to save
    const payloadItems = order
      .map((id) => nextMap.get(id))
      .filter(Boolean)
      .filter(isRealRow);
    persist(payloadItems);
  }, [activeTab, itemsById, order, persist]);

  const addNew = useCallback((statusValue = "active") => {
    const row = { ...emptyRow(), status: statusValue };
    pendingNewIdRef.current = row.id;
    setItemsById((prev) => new Map(prev).set(row.id, row));
    setOrder((prev) => [row.id, ...prev]);
    setOpenId(row.id); // open the new one
  }, []);

  const deleteOnSheet = async (id) => {
    if (!id) return;
    setStatus({ state: "loading", msg: "" });
    try {
      const r = await fetch("/api/listings/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Delete failed");

      setItemsById((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      setOrder((prev) => prev.filter((x) => x !== id));
      if (openId === id) setOpenId(null);

      try { localStorage.removeItem("khl-listings-cache-v1"); } catch {}

      setStatus({ state: "success", msg: "Deleted." });
    } catch (e) {
      setStatus({ state: "error", msg: e.message || "Delete failed." });
      if ((e.message || "").toLowerCase().includes("unauthorized")) setUnlocked(false);
    }
  };

  const retrySave = useCallback(() => {
    const payloadItems = order
      .map((id) => itemsById.get(id))
      .filter(Boolean)
      .filter(isRealRow);
    persist(payloadItems);
  }, [order, itemsById, persist]);

  const login = async () => {
    setPwError("");
    try {
      const r = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || "Login failed");
      setUnlocked(true);
      setPw("");
    } catch (e) {
      setPwError(e.message || "Login failed.");
    }
  };

  const logout = async () => {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include" }).catch(() => {});
    setUnlocked(false);
    setItemsById(new Map());
    setOrder([]);
    setOpenId(null);
  };

  const openItem = openId ? itemsById.get(openId) : null;

  const closeEditor = useCallback(() => {
    // Canceling a never-committed new row discards it entirely
    const pid = pendingNewIdRef.current;
    if (pid && pid === openId) {
      setItemsById((prev) => {
        const next = new Map(prev);
        next.delete(pid);
        return next;
      });
      setOrder((prev) => prev.filter((x) => x !== pid));
      pendingNewIdRef.current = null;
    }
    setOpenId(null);
  }, [openId]);

  return (
  <div className="interior-page listings-admin">
    <Navbar variant="contact" />

    {!unlocked ? (
      <header className="page-hero">
        <div className="interior-inner">
          <h2>Listings Admin</h2>
          <p className="subtitle">{checking ? "Checking session…" : "Enter password to continue."}</p>

          <div className="ui-card listings-admin__auth">
            <div className="listings-admin__auth-grid">
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Admin password"
                style={inputStyle}
                disabled={checking}
                onKeyDown={(e) => e.key === "Enter" && login()}
              />

              <button className="btn primary" type="button" onClick={login} disabled={checking}>
                Unlock
              </button>

              {pwError ? <div className="listings-admin__error">{pwError}</div> : null}
            </div>
          </div>
        </div>
      </header>
    ) : (
      <>
        <header className="page-hero">
          <div className="interior-inner">
            <h2>Listings Admin</h2>


            <div className="listings-admin__actions">
              <button className="btn secondary" type="button" onClick={() => addNew("active")} disabled={loading}>
                + Add Active
              </button>
              <button className="btn secondary" type="button" onClick={() => addNew("pending")} disabled={loading}>
                + Add Pending
              </button>
              <button className="btn secondary" type="button" onClick={() => addNew("sold")} disabled={loading}>
                + Add Sold
              </button>

              <button className="btn secondary" type="button" onClick={logout}>
                Logout
              </button>
            </div>

            {status.state !== "idle" ? (
              <div
                className={`status-banner ${
                  status.state === "success"
                    ? "status-banner--success"
                    : status.state === "error"
                    ? "status-banner--error"
                    : ""
                }`}
              >
                <span>{status.msg}</span>
                {status.state === "error" ? (
                  <button className="btn secondary small" type="button" onClick={retrySave}>
                    Retry Save
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </header>

        <main className="interior-inner listings-admin__main">
          <div className="listings-admin__toggle">
            <button
              className={`btn ${activeTab === "active" ? "primary" : "secondary"}`}
              type="button"
              onClick={() => {
                setActiveTab("active");
                setOpenId(null);
              }}
            >
              Active ({counts.active})
            </button>

            <button
              className={`btn ${activeTab === "sold" ? "primary" : "secondary"}`}
              type="button"
              onClick={() => {
                setActiveTab("sold");
                setOpenId(null);
              }}
            >
              Sold ({counts.sold})
            </button>
          </div>

          {loading ? (
            <div className="ui-card">
              <p style={{ margin: 0 }}>Loading…</p>
            </div>
          ) : (
            <div className="listings-admin__panel">
              <div className="listings-admin__list">
                {filtered.map((x) => (
                  <RowCard key={x.id} item={x} onOpen={setOpenId} onDelete={deleteOnSheet} />
                ))}
              </div>

              {!filtered.length ? (
                <div className="ui-card listings-admin__empty">
                  <p style={{ margin: 0 }}>No listings in this view.</p>
                </div>
              ) : null}
            </div>
          )}
        </main>

        {openItem ? (
          <EditorModal item={openItem} onClose={closeEditor} onCommit={commitItem} />
        ) : null}
      </>
    )}
  </div>
);
}